import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    step: '01',
    icon: 'UserPlusIcon',
    title: 'Create Your Account',
    body: 'Register in under 2 minutes. Your profile stores your handicap, preferences, and booking history.',
    color: 'text-accent',
    bg: 'bg-accent/10',
  },
  {
    step: '02',
    icon: 'CalendarDaysIcon',
    title: 'Pick Your Date & Time',
    body: 'Browse live tee sheet availability. Choose from morning, midday, or afternoon slots — 7 days a week.',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    step: '03',
    icon: 'CreditCardIcon',
    title: 'Pay Securely Online',
    body: 'Checkout via PayFast — South Africa\'s trusted payment gateway. Card, EFT, and instant EFT accepted.',
    color: 'text-accent',
    bg: 'bg-accent/10',
  },
  {
    step: '04',
    icon: 'CheckBadgeIcon',
    title: 'Arrive & Play',
    body: 'Receive instant email confirmation with your booking details. Show your QR code at the Pro Shop and tee off.',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
];

export default function BookingProcess() {
  return (
    <section className="py-20 bg-muted/50">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <span className="text-accent font-semibold text-sm tracking-widest uppercase">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-5 leading-tight">
              Book Your Round<br />
              <span className="text-primary">in 4 Simple Steps</span>
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-8">
              Our online booking system is open 24/7. No phone calls, no waiting — just pick your time, pay, and play. Instant confirmation every time.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'BoltIcon', label: 'Instant confirmation' },
                { icon: 'LockClosedIcon', label: 'Secure PayFast checkout' },
                { icon: 'ArrowPathIcon', label: 'Free cancellation' },
                { icon: 'DevicePhoneMobileIcon', label: 'Mobile-friendly' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-1.5 bg-card border border-border rounded-full px-3 py-1.5">
                  <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={13} variant="outline" className="text-primary" />
                  <span className="text-xs font-medium text-foreground">{b.label}</span>
                </div>
              ))}
            </div>

            <Link
              href="/online-booking"
              className="btn-gold px-8 py-3.5 rounded-xl text-sm font-bold inline-flex items-center gap-2"
            >
              <Icon name="CalendarDaysIcon" size={18} variant="outline" />
              Book a Tee Time Now
            </Link>
          </div>

          {/* Right: steps — 2x2 grid, not a vertical timeline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {steps.map((s) => (
              <div
                key={s.step}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200 relative overflow-hidden"
              >
                {/* Step number watermark */}
                <div className="absolute top-3 right-4 text-5xl font-extrabold text-border/60 leading-none select-none pointer-events-none">
                  {s.step}
                </div>
                <div className={`w-11 h-11 rounded-xl ${s.bg} flex items-center justify-center mb-4`}>
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={22} variant="outline" className={s.color} />
                </div>
                <h3 className="font-bold text-foreground text-sm mb-2 leading-snug">{s.title}</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
